/** A message with plural forms, picked by Intl.PluralRules; `{n}` is the count. */
export type Plural = Partial<Record<Intl.LDMLPluralRule, string>> & { other: string };

export const en = {
  'app.name': 'Ledgerline',
  'app.tagline': 'Subscription billing',
  'nav.overview': 'Overview',
  'nav.customers': 'Customers',
  'nav.invoices': 'Invoices',
  'nav.ledger': 'Ledger',
  'nav.webhooks': 'Webhooks',
  'nav.label': 'Sections',

  'clock.simulated': 'Simulated date',
  'clock.day': '+1 day',
  'clock.week': '+1 week',
  'clock.month': '+1 month',
  'clock.advancing': 'Running due work…',
  'clock.live': 'Live clock',
  'clock.disabled': 'The test clock is off on this server',
  'clock.utc': 'UTC',
  'summary.title': 'What happened',
  'summary.range': '{from} to {to}',
  'summary.nothing': 'Nothing was due.',
  'summary.days': 'Day by day',
  'summary.hideDays': 'Hide days',
  'summary.dismiss': 'Dismiss',
  'summary.errors': 'Errors',
  'trial.label': 'Trial balance',
  'trial.balanced': 'Balanced',
  'trial.unbalanced': 'Out of balance',
  'theme.dark': 'Dark theme',
  'theme.light': 'Light theme',
  'language.label': 'Language',

  'demo.note': 'Demo: everything runs in your browser.',
  'demo.reset': 'Reset',
  'demo.resetConfirm': 'Start over with a fresh six months of history?',
  'demo.notSaved': 'Your browser would not store the demo, so changes last until you close the tab.',
  'boot.title': 'Running six months of billing in your browser',
  'boot.progress': 'Day {day} of {days}',
  'boot.failed': 'The demo could not start: {message}',

  'auth.title': 'Connect to the Ledgerline API',
  'auth.server': 'Server',
  'auth.key': 'API key',
  'auth.hint': 'The key is kept in this browser only.',
  'auth.connect': 'Connect',
  'auth.refused': 'The server did not accept this key.',
  'auth.unreachable': 'The server could not be reached: {message}',
  'auth.signOut': 'Forget API key',

  'common.loading': 'Loading…',
  'common.error': 'Could not load this: {message}',
  'common.retry': 'Try again',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.page': 'Page {page}',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.all': 'All',
  'common.none': 'None',
  'common.notFound': 'Nothing here.',
  'common.failed': 'This page could not be shown.',
  'common.backTo': 'Back to {page}',
  'common.currency': 'Currency',
  'common.amount': 'Amount',
  'common.status': 'Status',
  'common.date': 'Date',
  'common.id': 'ID',
  'common.period': 'Period',
  'common.created': 'Created',
  'common.customer': 'Customer',
  'common.plan': 'Plan',
  'common.showTable': 'Show as table',
  'common.hideTable': 'Hide table',

  'status.trialing': 'Trialing',
  'status.active': 'Active',
  'status.past_due': 'Past due',
  'status.canceled': 'Canceled',
  'status.draft': 'Draft',
  'status.open': 'Open',
  'status.paid': 'Paid',
  'status.void': 'Void',
  'status.uncollectible': 'Uncollectible',
  'status.pending': 'Pending',
  'status.succeeded': 'Succeeded',
  'status.failed': 'Failed',

  'trigger.initial': 'First attempt',
  'trigger.scheduled_retry': 'Scheduled retry',
  'trigger.payment_method_updated': 'New card',
  'failure.card_declined': 'Card declined',
  'failure.insufficient_funds': 'Insufficient funds',
  'failure.payment_method_missing': 'No card on file',

  'posting.invoice_finalized': 'Invoice issued',
  'posting.payment_succeeded': 'Payment received',
  'posting.downgrade_credit': 'Downgrade credit',
  'posting.credit_applied': 'Credit applied',
  'posting.write_off': 'Write-off',
  'posting.refund': 'Refund',
  'posting.overpayment_credit': 'Overpayment kept as credit',
  'posting.reversal': 'Reversal',

  'account.accounts_receivable': 'Accounts receivable',
  'account.customer_credit': 'Customer credit',
  'account.revenue': 'Subscription revenue',
  'account.provider_clearing': 'Payrail clearing',
  'account.bad_debt': 'Bad debt expense',

  'invoiceKind.proration': 'Proration',
  'line.plan': '{plan}',
  'line.proration_credit': 'Unused time on {plan}',
  'line.proration_charge': 'Remaining time on {plan}',
  'interval.month': 'per month',
  'interval.year': 'per year',

  'overview.title': 'Overview',
  'overview.mrr': 'MRR, {currency}',
  'overview.active': 'Active',
  'overview.trialing': 'Trialing',
  'overview.pastDue': 'Past due',
  'overview.churn': 'Churned, 30 days',
  'overview.revenue': 'Revenue by day, {currency}',
  'overview.revenueHint': 'Net credits to revenue: invoices issued, less downgrade credits and refunds.',
  'overview.days': '{days} days',
  'overview.noRevenue': 'No revenue in this period.',
  'overview.latest': 'Latest events',
  'overview.attention': 'Past due',
  'overview.nextRetry': 'Next retry',
  'overview.attempts': 'Attempts',
  'overview.noPastDue': 'No past-due subscriptions.',
  'overview.dunningOver': 'Retries used up',
  'overview.comingUp': 'Coming up',
  'overview.comingUpHint': 'What the engine does next as the clock moves.',
  'overview.nothingSoon': 'Nothing is due in the next 30 days.',
  'upcoming.trialEnds': 'Trial ends',
  'upcoming.renews': 'Renews',
  'upcoming.cancels': 'Cancels',
  'upcoming.retry': 'Payment retry',

  'customers.title': 'Customers',
  'customers.search': 'Search by name or email',
  'customers.price': 'Price',
  'customers.nextEvent': 'Renews or ends',
  'customers.noSubscription': 'No subscription',
  'customers.empty': 'No customers match.',
  'customers.count': { one: '{n} customer', other: '{n} customers' } satisfies Plural,

  'customer.subscription': 'Subscription',
  'customer.currentPeriod': 'Current period',
  'customer.trialEnds': 'Trial ends',
  'customer.cancelsOn': 'Cancels on {date}',
  'customer.endedOn': 'Ended on {date}',
  'customer.changePlan': 'Change plan',
  'customer.cancel': 'Cancel at period end',
  'customer.resume': 'Resume',
  'customer.history': 'History',
  'customer.paymentMethods': 'Payment methods',
  'customer.attachCard': 'Attach test card',
  'customer.default': 'Default',
  'customer.makeDefault': 'Make default',
  'customer.expires': 'Expires {date}',
  'customer.noCards': 'No card on file.',
  'customer.invoices': 'Invoices',
  'customer.noInvoices': 'No invoices yet.',
  'customer.dunning': 'Dunning',
  'customer.nextRetry': 'Attempts: {attempts} · next retry {date}',
  'customer.retriesUsed': 'Attempts: {attempts} · no retries left',
  'customer.balances': 'Account balances',
  'customer.balancesIn': 'From the ledger, in {currency}',
  'customer.credit': 'Credit available',
  'customer.since': 'Customer since {date}',

  'history.subscribed': 'Subscribed to {plan}',
  'history.trialEnded': 'Trial ended',
  'history.renewed': 'Billed for {period}',
  'history.prorated': 'Plan change billed for the rest of the period',
  'history.downgradeCredit': 'Downgrade: unused time credited',
  'history.paymentFailed': 'Payment failed, subscription past due',
  'history.writtenOff': 'Invoice written off after the last retry',
  'history.cancelRequested': 'Set to cancel at the period end',
  'history.canceled': 'Subscription ended',

  'planChange.title': 'Change plan',
  'planChange.current': 'Current plan: {plan}.',
  'planChange.to': 'New plan',
  'planChange.choose': 'Choose a plan',
  'planChange.preview': 'Preview from the billing engine',
  'planChange.changeAt': 'Takes effect {date}',
  'planChange.credit': 'Credit for unused time',
  'planChange.charge': 'Charge for the rest of the period',
  'planChange.net': 'Net',
  'planChange.creditApplied': 'Customer credit',
  'planChange.chargedNow': 'Charged to the card now',
  'planChange.paidFromCredit': 'Invoiced now and paid from customer credit.',
  'planChange.confirm': 'Change plan',
  'planChange.effect.invoice_now': 'Invoiced and charged now.',
  'planChange.effect.customer_credit': 'Becomes customer credit that the next invoice spends.',
  'planChange.effect.no_charge': 'No money moves.',
  'planChange.effect.trial_switch': 'The trial continues on the new plan; nothing is charged.',
  'planChange.sameInterval': 'Plans with another billing interval are not offered: a change keeps the interval.',

  'card.title': 'Attach a test card',
  'card.succeeds': 'Payments succeed',
  'card.declined': 'Card is declined',
  'card.insufficient': 'Insufficient funds',
  'card.makeDefault': 'Make it the default card',
  'card.retryNote': 'A new default card retries past-due invoices at once.',
  'card.attach': 'Attach card',

  'invoices.title': 'Invoices',
  'invoices.number': 'Number',
  'invoices.total': 'Total',
  'invoices.credit': 'Credit',
  'invoices.due': 'Due',
  'invoices.paid': 'Paid',
  'invoices.issued': 'Issued',
  'invoices.empty': 'No invoices with this status.',

  'invoice.title': 'Invoice {number}',
  'invoice.label': 'Invoice',
  'invoice.draft': 'Draft invoice',
  'invoice.billTo': 'Bill to',
  'invoice.issued': 'Issued',
  'invoice.period': 'Service period',
  'invoice.paidAt': 'Paid',
  'invoice.description': 'Description',
  'invoice.total': 'Total',
  'invoice.creditApplied': 'Customer credit applied',
  'invoice.amountDue': 'Amount due',
  'invoice.amountPaid': 'Paid',
  'invoice.refunded': 'Refunded',
  'invoice.outstanding': 'Outstanding',
  'invoice.payments': 'Payments and attempts',
  'invoice.attempt': 'Attempt',
  'invoice.trigger': 'Trigger',
  'invoice.failure': 'Failure',
  'invoice.providerId': 'Payrail ID',
  'invoice.noPayments': 'No payment was needed: credit covered the invoice.',
  'invoice.timeline': 'Status timeline',
  'invoice.print': 'Print',
  'timeline.created': 'Created',
  'timeline.finalized': 'Issued as {number}',
  'timeline.attemptFailed': 'Attempt {attempt} failed: {reason}',
  'timeline.attemptSucceeded': 'Attempt {attempt} succeeded',
  'timeline.attemptPending': 'Attempt {attempt} sent to Payrail',
  'timeline.paid': 'Paid in full',
  'timeline.uncollectible': 'Written off as uncollectible',
  'timeline.refunded': 'Refunded',
  'timeline.retryScheduled': 'Next retry scheduled',

  'ledger.title': 'Ledger',
  'ledger.journal': 'Journal',
  'ledger.accounts': 'Accounts',
  'ledger.trialBalance': 'Trial balance',
  'ledger.transaction': 'Transaction',
  'ledger.account': 'Account',
  'ledger.debit': 'Debit',
  'ledger.credit': 'Credit',
  'ledger.debits': 'Debits',
  'ledger.credits': 'Credits',
  'ledger.balance': 'Balance',
  'ledger.kind': 'Kind',
  'ledger.allKinds': 'All kinds',
  'ledger.net': 'Debits minus credits',
  'ledger.explainBalanced':
    'Every journal transaction balances, so in each currency all debits equal all credits. Anything but zero would mean a bug.',
  'ledger.empty': 'No journal transactions.',
  'ledger.forInvoice': 'Invoice',

  'webhooks.title': 'Webhooks',
  'webhooks.subtitle': 'Signed Payrail events received by the API, newest first.',
  'webhooks.received': 'Received',
  'webhooks.type': 'Type',
  'webhooks.payment': 'Payment',
  'webhooks.outcome': 'Outcome',
  'webhooks.meaning': 'What it did',
  'webhooks.empty': 'No events received yet.',
  'webhook.processed': 'Processed',
  'webhook.duplicate': 'Duplicate',
  'webhook.ignored': 'Ignored',
  'webhookOutcome.applied': 'Settled the payment',
  'webhookOutcome.duplicate_payment_update': 'Already settled from the API answer',
  'webhookOutcome.stale_payment_update': 'Older than the recorded status',
  'webhookOutcome.unmatched': 'No matching payment',
  'webhookOutcome.ignored_type': 'No handler for this event type',
  'webhookOutcome.malformed': 'Payload not understood',

  'count.renewals': { one: '{n} renewal', other: '{n} renewals' } satisfies Plural,
  'count.trialsEnded': { one: '{n} trial ended', other: '{n} trials ended' } satisfies Plural,
  'count.paymentsSucceeded': { one: '{n} payment collected', other: '{n} payments collected' } satisfies Plural,
  'count.paymentsFailed': { one: '{n} payment failed', other: '{n} payments failed' } satisfies Plural,
  'count.retries': { one: '{n} retry', other: '{n} retries' } satisfies Plural,
  'count.canceled': { one: '{n} cancellation', other: '{n} cancellations' } satisfies Plural,
  'count.writtenOff': { one: '{n} invoice written off', other: '{n} invoices written off' } satisfies Plural,
  'count.recovered': { one: '{n} lost answer recovered', other: '{n} lost answers recovered' } satisfies Plural,

  'error.interval_mismatch': 'A plan change keeps the billing interval.',
  'error.same_plan': 'The subscription is already on this plan.',
  'error.subscription_not_changeable': 'Only active or trialing subscriptions can change plans.',
  'error.plan_unavailable': 'This plan is not sold in the customer’s currency.',
  'error.card_not_supported': 'Payrail test mode accepts only the test cards listed here.',
  'error.not_found': 'Not found.',
  'error.network_error': 'The API could not be reached.',
};

export type MessageKey = keyof typeof en;
export type Messages = { [K in MessageKey]: (typeof en)[K] extends Plural ? Plural : string };

export const ru: Messages = {
  'app.name': 'Ledgerline',
  'app.tagline': 'Биллинг подписок',
  'nav.overview': 'Обзор',
  'nav.customers': 'Клиенты',
  'nav.invoices': 'Счета',
  'nav.ledger': 'Главная книга',
  'nav.webhooks': 'Вебхуки',
  'nav.label': 'Разделы',

  'clock.simulated': 'Дата симуляции',
  'clock.day': '+1 день',
  'clock.week': '+1 неделя',
  'clock.month': '+1 месяц',
  'clock.advancing': 'Движок обрабатывает дни…',
  'clock.live': 'Реальное время',
  'clock.disabled': 'Тестовые часы на этом сервере выключены',
  'clock.utc': 'UTC',
  'summary.title': 'Что произошло',
  'summary.range': 'с {from} по {to}',
  'summary.nothing': 'Ничего не происходило.',
  'summary.days': 'По дням',
  'summary.hideDays': 'Скрыть дни',
  'summary.dismiss': 'Скрыть',
  'summary.errors': 'Ошибки',
  'trial.label': 'Дебет − кредит',
  'trial.balanced': 'Сходится',
  'trial.unbalanced': 'Не сходится',
  'theme.dark': 'Тёмная тема',
  'theme.light': 'Светлая тема',
  'language.label': 'Язык',

  'demo.note': 'Демо: всё работает в вашем браузере.',
  'demo.reset': 'Сбросить',
  'demo.resetConfirm': 'Начать заново с новой историей за полгода?',
  'demo.notSaved': 'Браузер не сохранил демо: изменения пропадут, когда вы закроете вкладку.',
  'boot.title': 'Прогоняем полгода биллинга в вашем браузере',
  'boot.progress': 'День {day} из {days}',
  'boot.failed': 'Демо не запустилось: {message}',

  'auth.title': 'Подключение к API Ledgerline',
  'auth.server': 'Сервер',
  'auth.key': 'API-ключ',
  'auth.hint': 'Ключ хранится только в этом браузере.',
  'auth.connect': 'Подключиться',
  'auth.refused': 'Сервер не принял этот ключ.',
  'auth.unreachable': 'Сервер недоступен: {message}',
  'auth.signOut': 'Забыть API-ключ',

  'common.loading': 'Загрузка…',
  'common.error': 'Не удалось загрузить: {message}',
  'common.retry': 'Повторить',
  'common.previous': 'Назад',
  'common.next': 'Вперёд',
  'common.page': 'Страница {page}',
  'common.cancel': 'Отмена',
  'common.close': 'Закрыть',
  'common.all': 'Все',
  'common.none': 'Нет',
  'common.notFound': 'Здесь пусто.',
  'common.failed': 'Не удалось показать страницу.',
  'common.backTo': 'Назад: {page}',
  'common.currency': 'Валюта',
  'common.amount': 'Сумма',
  'common.status': 'Статус',
  'common.date': 'Дата',
  'common.id': 'ID',
  'common.period': 'Период',
  'common.created': 'Создан',
  'common.customer': 'Клиент',
  'common.plan': 'Тариф',
  'common.showTable': 'Показать таблицей',
  'common.hideTable': 'Скрыть таблицу',

  'status.trialing': 'Пробный период',
  'status.active': 'Активна',
  'status.past_due': 'Просрочена',
  'status.canceled': 'Отменена',
  'status.draft': 'Черновик',
  'status.open': 'Открыт',
  'status.paid': 'Оплачен',
  'status.void': 'Аннулирован',
  'status.uncollectible': 'Безнадёжный',
  'status.pending': 'В обработке',
  'status.succeeded': 'Прошёл',
  'status.failed': 'Не прошёл',

  'trigger.initial': 'Первая попытка',
  'trigger.scheduled_retry': 'Повтор по графику',
  'trigger.payment_method_updated': 'Новая карта',
  'failure.card_declined': 'Карта отклонена',
  'failure.insufficient_funds': 'Недостаточно средств',
  'failure.payment_method_missing': 'Нет карты',

  'posting.invoice_finalized': 'Выставлен счёт',
  'posting.payment_succeeded': 'Получен платёж',
  'posting.downgrade_credit': 'Кредит за понижение тарифа',
  'posting.credit_applied': 'Зачтён кредит',
  'posting.write_off': 'Списание долга',
  'posting.refund': 'Возврат',
  'posting.overpayment_credit': 'Переплата зачтена в кредит',
  'posting.reversal': 'Сторно',

  'account.accounts_receivable': 'Дебиторская задолженность',
  'account.customer_credit': 'Кредит клиента',
  'account.revenue': 'Выручка от подписок',
  'account.provider_clearing': 'Транзитный счёт Payrail',
  'account.bad_debt': 'Безнадёжные долги',

  'invoiceKind.proration': 'Перерасчёт',
  'line.plan': '{plan}',
  'line.proration_credit': 'Неиспользованное время: {plan}',
  'line.proration_charge': 'Оставшееся время: {plan}',
  'interval.month': 'в месяц',
  'interval.year': 'в год',

  'overview.title': 'Обзор',
  'overview.mrr': 'MRR, {currency}',
  'overview.active': 'Активные',
  'overview.trialing': 'На пробном',
  'overview.pastDue': 'Просроченные',
  'overview.churn': 'Ушли за 30 дней',
  'overview.revenue': 'Выручка по дням, {currency}',
  'overview.revenueHint': 'Чистый оборот по выручке: выставленные счета минус кредиты за понижение и возвраты.',
  'overview.days': '{days} дней',
  'overview.noRevenue': 'За этот период выручки нет.',
  'overview.latest': 'Последние события',
  'overview.attention': 'Просрочены',
  'overview.nextRetry': 'Следующий повтор',
  'overview.attempts': 'Попыток',
  'overview.noPastDue': 'Просроченных подписок нет.',
  'overview.dunningOver': 'Повторы исчерпаны',
  'overview.comingUp': 'Скоро',
  'overview.comingUpHint': 'Что движок сделает, когда часы пойдут вперёд.',
  'overview.nothingSoon': 'В ближайшие 30 дней ничего не наступает.',
  'upcoming.trialEnds': 'Конец пробного',
  'upcoming.renews': 'Продление',
  'upcoming.cancels': 'Отмена',
  'upcoming.retry': 'Повтор платежа',

  'customers.title': 'Клиенты',
  'customers.search': 'Поиск по имени или email',
  'customers.price': 'Цена',
  'customers.nextEvent': 'Продление или конец',
  'customers.noSubscription': 'Нет подписки',
  'customers.empty': 'Никого не нашлось.',
  'customers.count': { one: '{n} клиент', few: '{n} клиента', many: '{n} клиентов', other: '{n} клиента' },

  'customer.subscription': 'Подписка',
  'customer.currentPeriod': 'Текущий период',
  'customer.trialEnds': 'Пробный период до',
  'customer.cancelsOn': 'Отменится {date}',
  'customer.endedOn': 'Завершена {date}',
  'customer.changePlan': 'Сменить тариф',
  'customer.cancel': 'Отменить в конце периода',
  'customer.resume': 'Возобновить',
  'customer.history': 'История',
  'customer.paymentMethods': 'Способы оплаты',
  'customer.attachCard': 'Привязать тестовую карту',
  'customer.default': 'Основная',
  'customer.makeDefault': 'Сделать основной',
  'customer.expires': 'Действует до {date}',
  'customer.noCards': 'Карты нет.',
  'customer.invoices': 'Счета',
  'customer.noInvoices': 'Счетов пока нет.',
  'customer.dunning': 'Повторные списания',
  'customer.nextRetry': 'Попыток: {attempts} · следующий повтор {date}',
  'customer.retriesUsed': 'Попыток: {attempts} · повторов не осталось',
  'customer.balances': 'Остатки по счетам учёта',
  'customer.balancesIn': 'По главной книге, в {currency}',
  'customer.credit': 'Доступный кредит',
  'customer.since': 'Клиент с {date}',

  'history.subscribed': 'Подписка на {plan}',
  'history.trialEnded': 'Пробный период закончился',
  'history.renewed': 'Выставлен счёт за {period}',
  'history.prorated': 'Смена тарифа: доплата до конца периода',
  'history.downgradeCredit': 'Понижение тарифа: остаток зачтён в кредит',
  'history.paymentFailed': 'Платёж не прошёл, подписка просрочена',
  'history.writtenOff': 'После последнего повтора счёт списан',
  'history.cancelRequested': 'Отмена в конце периода',
  'history.canceled': 'Подписка завершена',

  'planChange.title': 'Смена тарифа',
  'planChange.current': 'Текущий тариф: {plan}.',
  'planChange.to': 'Новый тариф',
  'planChange.choose': 'Выберите тариф',
  'planChange.preview': 'Расчёт биллингового движка',
  'planChange.changeAt': 'Вступает в силу {date}',
  'planChange.credit': 'Кредит за неиспользованное время',
  'planChange.charge': 'Начисление до конца периода',
  'planChange.net': 'Итого',
  'planChange.creditApplied': 'Кредит клиента',
  'planChange.chargedNow': 'Списывается с карты сейчас',
  'planChange.paidFromCredit': 'Счёт выставляется сейчас и оплачивается из кредита клиента.',
  'planChange.confirm': 'Сменить тариф',
  'planChange.effect.invoice_now': 'Счёт выставляется и оплачивается сразу.',
  'planChange.effect.customer_credit': 'Станет кредитом клиента и уйдёт в следующий счёт.',
  'planChange.effect.no_charge': 'Деньги не двигаются.',
  'planChange.effect.trial_switch': 'Пробный период продолжается на новом тарифе, без списаний.',
  'planChange.sameInterval': 'Тарифы с другим периодом оплаты не предлагаются: смена тарифа его сохраняет.',

  'card.title': 'Привязать тестовую карту',
  'card.succeeds': 'Платежи проходят',
  'card.declined': 'Карта отклоняется',
  'card.insufficient': 'Недостаточно средств',
  'card.makeDefault': 'Сделать основной картой',
  'card.retryNote': 'Новая основная карта сразу повторяет просроченные счета.',
  'card.attach': 'Привязать карту',

  'invoices.title': 'Счета',
  'invoices.number': 'Номер',
  'invoices.total': 'Сумма',
  'invoices.credit': 'Кредит',
  'invoices.due': 'К оплате',
  'invoices.paid': 'Оплачено',
  'invoices.issued': 'Выставлен',
  'invoices.empty': 'Счетов с таким статусом нет.',

  'invoice.title': 'Счёт {number}',
  'invoice.label': 'Счёт',
  'invoice.draft': 'Черновик счёта',
  'invoice.billTo': 'Плательщик',
  'invoice.issued': 'Выставлен',
  'invoice.period': 'Период услуг',
  'invoice.paidAt': 'Оплачен',
  'invoice.description': 'Описание',
  'invoice.total': 'Итого',
  'invoice.creditApplied': 'Зачтён кредит клиента',
  'invoice.amountDue': 'К оплате',
  'invoice.amountPaid': 'Оплачено',
  'invoice.refunded': 'Возвращено',
  'invoice.outstanding': 'Остаток долга',
  'invoice.payments': 'Платежи и попытки',
  'invoice.attempt': 'Попытка',
  'invoice.trigger': 'Причина',
  'invoice.failure': 'Ошибка',
  'invoice.providerId': 'ID в Payrail',
  'invoice.noPayments': 'Платёж не понадобился: счёт закрыт кредитом.',
  'invoice.timeline': 'Хронология',
  'invoice.print': 'Печать',
  'timeline.created': 'Создан',
  'timeline.finalized': 'Выставлен под номером {number}',
  'timeline.attemptFailed': 'Попытка {attempt} не прошла: {reason}',
  'timeline.attemptSucceeded': 'Попытка {attempt} прошла',
  'timeline.attemptPending': 'Попытка {attempt} отправлена в Payrail',
  'timeline.paid': 'Оплачен полностью',
  'timeline.uncollectible': 'Списан как безнадёжный',
  'timeline.refunded': 'Возвращён',
  'timeline.retryScheduled': 'Запланирован повтор',

  'ledger.title': 'Главная книга',
  'ledger.journal': 'Журнал',
  'ledger.accounts': 'Счета учёта',
  'ledger.trialBalance': 'Оборотная ведомость',
  'ledger.transaction': 'Операция',
  'ledger.account': 'Счёт',
  'ledger.debit': 'Дебет',
  'ledger.credit': 'Кредит',
  'ledger.debits': 'Дебет',
  'ledger.credits': 'Кредит',
  'ledger.balance': 'Сальдо',
  'ledger.kind': 'Тип',
  'ledger.allKinds': 'Все типы',
  'ledger.net': 'Дебет минус кредит',
  'ledger.explainBalanced':
    'Каждая проводка сбалансирована, поэтому в каждой валюте сумма дебета равна сумме кредита. Всё, кроме нуля, означало бы ошибку.',
  'ledger.empty': 'Проводок нет.',
  'ledger.forInvoice': 'Счёт',

  'webhooks.title': 'Вебхуки',
  'webhooks.subtitle': 'Подписанные события Payrail, полученные API, новые сверху.',
  'webhooks.received': 'Получено',
  'webhooks.type': 'Тип',
  'webhooks.payment': 'Платёж',
  'webhooks.outcome': 'Результат',
  'webhooks.meaning': 'Что сделано',
  'webhooks.empty': 'Событий пока нет.',
  'webhook.processed': 'Обработано',
  'webhook.duplicate': 'Дубликат',
  'webhook.ignored': 'Пропущено',
  'webhookOutcome.applied': 'Провело результат платежа',
  'webhookOutcome.duplicate_payment_update': 'Уже учтено по ответу API',
  'webhookOutcome.stale_payment_update': 'Старее записанного статуса',
  'webhookOutcome.unmatched': 'Платёж не найден',
  'webhookOutcome.ignored_type': 'Нет обработчика для этого типа',
  'webhookOutcome.malformed': 'Непонятный формат',

  'count.renewals': { one: '{n} продление', few: '{n} продления', many: '{n} продлений', other: '{n} продления' },
  'count.trialsEnded': { one: 'закончился {n} пробный период', few: 'закончились {n} пробных периода', many: 'закончились {n} пробных периодов', other: 'закончились {n} пробного периода' },
  'count.paymentsSucceeded': { one: '{n} платёж прошёл', few: '{n} платежа прошли', many: '{n} платежей прошли', other: '{n} платежа прошли' },
  'count.paymentsFailed': { one: '{n} платёж не прошёл', few: '{n} платежа не прошли', many: '{n} платежей не прошли', other: '{n} платежа не прошли' },
  'count.retries': { one: '{n} повтор', few: '{n} повтора', many: '{n} повторов', other: '{n} повтора' },
  'count.canceled': { one: '{n} отмена', few: '{n} отмены', many: '{n} отмен', other: '{n} отмены' },
  'count.writtenOff': { one: '{n} счёт списан', few: '{n} счёта списаны', many: '{n} счетов списаны', other: '{n} счёта списаны' },
  'count.recovered': { one: '{n} потерянный ответ восстановлен', few: '{n} потерянных ответа восстановлены', many: '{n} потерянных ответов восстановлены', other: '{n} потерянного ответа восстановлены' },

  'error.interval_mismatch': 'Смена тарифа сохраняет период оплаты.',
  'error.same_plan': 'Подписка уже на этом тарифе.',
  'error.subscription_not_changeable': 'Сменить тариф можно только у активной подписки или на пробном периоде.',
  'error.plan_unavailable': 'Этот тариф не продаётся в валюте клиента.',
  'error.card_not_supported': 'Тестовый режим Payrail принимает только перечисленные тестовые карты.',
  'error.not_found': 'Не найдено.',
  'error.network_error': 'API недоступен.',
};
